import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Row, Col, Dropdown, Space, Divider, Input, Radio, Select, Form, Image } from "antd";
// import type { MenuProps } from "antd";
import {
  CloseCircleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  EditOutlined,
  AimOutlined,
  PlusOutlined,
  SaveOutlined,
  CheckOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { allMaterialsList } from "../../constants/materials";
import "./MaterialForm.css";
import MetalForm from "../MetalForm/MetalForm";
const { TextArea } = Input;

export interface MaterialFormProps {
  type: "purchase" | "shipment" | "sale";
  scaleSerialPort: any;
  showForm: boolean;
  metals: any[];
  categories: any[];
  closeForm: () => void;
  addToMaterialsList: (materialInfo: any) => void;
  saveToMaterialsList: (materialInfo: any) => void;
  recordMaterial: () => any;
  editMaterialData: any;
  resetEditMaterialData: () => void;
  postMetal?: (metalInfo: any) => void;
  getMetalsForm: () => void;
  captureScaleImage: () => any;
  captureMaterialImage: () => any;
  resetImageView: () => void;
  scaleImageSrc: string;
  materialImageSrc: string;
}

const MaterialForm = (props: MaterialFormProps) => {
  const [materialInfo, setMaterialInfo] = useState({
    materialID: uuidv4(),
    name: "",
    displayName: "",
    metalID: "",
    rawWeight: "0.0",
    wasteWeight: "0.0",
    netWeight: "0.0",
    manualEntry: false,
    smallBag: 0,
    bigBag: 0,
    jumboBag: 0,
    notes: "",
    other: "",
    updateWasteWeight: false,
    scaleImageSrc: "",
    materialImageSrc: "",
    packageType: "bags",
  });
  const [enableManualEntry, setEnableManualEntry] = useState(false);
  const [rawWeightFormStatus, setRawWeightFormStatus] = useState("") as any;
  const [rawWeightFormMessage, setRawWeightFormMessage] = useState("");
  const [wasteWeightFormStatus, setWasteWeightFormStatus] = useState("") as any;
  const [wasteWeightFormMessage, setWasteWeightFormMessage] = useState("");
  const [materialNameFormStatus, setMaterialNameFormStatus] = useState("") as any;
  const [materialNameFormMessage, setMaterialNameFormMessage] = useState("");
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [materialsNameList, setMaterialsNameList] = useState([]) as any;
  const [showMetalForm, setShowMetalForm] = useState(false);
  const weightRegEx = /^\d+\.\d{1}$/;
  // const [scaleImage, setScaleImage] = useState(null);
  // const [materialPackageType, setMaterialPackageType] = useState("bags");

  useEffect(() => {
    generateMaterialsList();
    isManualEntry();
    if (props.editMaterialData) loadMaterialData();
  }, [props.scaleSerialPort, props.editMaterialData, props.metals]);

  const isManualEntry = () => {
    if (props.scaleSerialPort === null || props.scaleSerialPort === undefined) {
      setEnableManualEntry(true);
      return;
    }
    setEnableManualEntry(false);
  };

  const loadMaterialData = () => {
    setMaterialInfo(props.editMaterialData);
  };

  const generateMaterialsList = () => {
    const sortedAllMaterialsList = props.metals;
    const sortedAllMaterialsListDropdown = sortedAllMaterialsList.map((metal: any) => {
      return {
        label: metal.displayName,
        value: metal.metalID,
      };
    });
    setMaterialsNameList(sortedAllMaterialsListDropdown);
  };

  const updateMaterialName = (metalID: any) => {
    const selectedMaterial = props.metals.find((metal: any) => metal.metalID === metalID);
    setMaterialNameFormStatus("");
    setMaterialNameFormMessage("");
    let material = { ...materialInfo };
    material.name = selectedMaterial.name;
    material.displayName = selectedMaterial.displayName;
    material.metalID = selectedMaterial.metalID;
    setMaterialInfo(material);
  };

  const updateRawWeight = (rawWeight: string, manualEntry: boolean) => {
    if (Number(rawWeight) <= 0.0) {
      setRawWeightFormStatus("error");
      setRawWeightFormMessage("Must be more than 0.0 kg");
    } else if (!weightRegEx.test(rawWeight)) {
      setRawWeightFormStatus("error");
      setRawWeightFormMessage("Weight must have 1 decimal place. Example 0.0 kg");
    } else {
      setRawWeightFormStatus("");
      setRawWeightFormMessage("");
    }
    let material = { ...materialInfo };
    const netWeight = Number(rawWeight) - Number(material.wasteWeight);
    material.rawWeight = rawWeight;
    material.netWeight = netWeight.toFixed(1);
    material.manualEntry = manualEntry;
    setMaterialInfo(material);
  };

  const updateWasteWeight = (wasteWeight: string) => {
    setWasteWeightFormStatus("");
    setWasteWeightFormMessage("");
    if (!weightRegEx.test(wasteWeight)) {
      setWasteWeightFormStatus("error");
      setWasteWeightFormMessage("Weight must have 1 decimal place. Example 0.0 kg");
    }
    let material = { ...materialInfo };
    const netWeight = Number(material.rawWeight) - Number(wasteWeight);
    material.wasteWeight = wasteWeight;
    // material.wasteWeight = Number(wasteWeight).toFixed(1);
    material.netWeight = netWeight.toFixed(1);
    setMaterialInfo(material);
  };

  const closeForm = () => {
    resetMaterialInfo();
    isManualEntry();
    setRawWeightFormStatus("");
    setRawWeightFormMessage("");
    setWasteWeightFormStatus("");
    setWasteWeightFormMessage("");
    setMaterialNameFormStatus("");
    setMaterialNameFormMessage("");
    props.resetEditMaterialData();
    props.resetImageView();
    props.closeForm();
  };

  const validateForm = () => {
    let material = { ...materialInfo };
    let validationFormStatus = true;
    if (material.name === "") {
      validationFormStatus = false;
      setMaterialNameFormStatus("error");
      setMaterialNameFormMessage("Select a material");
    }
    if (Number(material.rawWeight) <= 0.0) {
      validationFormStatus = false;
      setRawWeightFormStatus("error");
      setRawWeightFormMessage("Must be more than 0.0 kg");
    }
    if (Number(material.wasteWeight) < 0.0) {
      validationFormStatus = false;
      setWasteWeightFormStatus("error");
      setWasteWeightFormMessage("Cannot be lower than 0.0 kg");
    }
    if (!weightRegEx.test(material.rawWeight)) {
      validationFormStatus = false;
      setRawWeightFormStatus("error");
      setRawWeightFormMessage("Weight must have 1 decimal place. Example 0.0 kg");
    }
    if (!weightRegEx.test(material.wasteWeight)) {
      validationFormStatus = false;
      setWasteWeightFormStatus("error");
      setWasteWeightFormMessage("Weight must have 1 decimal place. Example 0.0 kg");
    }
    return validationFormStatus;
  };

  const addToMaterialsList = () => {
    props.addToMaterialsList(materialInfo);
    resetMaterialInfo();
    isManualEntry();
    setRawWeightFormStatus("");
    setRawWeightFormMessage("");
    setWasteWeightFormStatus("");
    setWasteWeightFormMessage("");
    setMaterialNameFormStatus("");
    setMaterialNameFormMessage("");
  };

  const saveToMaterialsList = () => {
    props.saveToMaterialsList(materialInfo);
    resetMaterialInfo();
    isManualEntry();
    setRawWeightFormStatus("");
    setRawWeightFormMessage("");
    setWasteWeightFormStatus("");
    setWasteWeightFormMessage("");
    setMaterialNameFormStatus("");
    setMaterialNameFormMessage("");
  };

  const updateManualEntry = () => {
    let material = { ...materialInfo };
    material.manualEntry = true;
    setMaterialInfo(material);
    setEnableManualEntry(true);
  };

  const recordWeightEntry = () => {
    setEnableManualEntry(false);
    let rawMaterialInfo: any = props.recordMaterial();
    updateRawWeight(rawMaterialInfo, false);
  };

  const captureImages = () => {
    let scaleImage = props.captureScaleImage();
    let materialImage = props.captureMaterialImage();
  };

  const increaseBagCount = (bagType: string) => {
    let material = { ...materialInfo };
    let currCount = 0;
    switch (bagType) {
      case "smallBag":
        currCount = Number(material.smallBag);
        currCount += 1;
        material.smallBag = currCount;
        setMaterialInfo(material);
        break;
      case "bigBag":
        currCount = Number(material.bigBag);
        currCount += 1;
        material.bigBag = currCount;
        setMaterialInfo(material);
        break;
      case "jumboBag":
        currCount = Number(material.jumboBag);
        currCount += 1;
        material.jumboBag = currCount;
        setMaterialInfo(material);
        break;
    }
  };

  const decreaseBagCount = (bagType: string) => {
    let material = { ...materialInfo };
    let currCount = 0;
    switch (bagType) {
      case "smallBag":
        if (material.smallBag > 0) {
          currCount = Number(material.smallBag);
          currCount -= 1;
          material.smallBag = currCount;
          setMaterialInfo(material);
          break;
        }
        break;
      case "bigBag":
        if (material.bigBag > 0) {
          currCount = Number(material.bigBag);
          currCount -= 1;
          material.bigBag = currCount;
          setMaterialInfo(material);
          break;
        }
        break;
      case "jumboBag":
        if (material.jumboBag > 0) {
          currCount = Number(material.jumboBag);
          currCount -= 1;
          material.jumboBag = currCount;
          setMaterialInfo(material);
          break;
        }
        break;
    }
  };

  const updateBagInputCount = (bagType: string, count: string) => {
    let material = { ...materialInfo };
    switch (bagType) {
      case "smallBag":
        material.smallBag = Number(count);
        break;
      case "bigBag":
        material.bigBag = Number(count);
        break;
      case "jumboBag":
        material.jumboBag = Number(count);
        break;
    }
    setMaterialInfo(material);
  };

  const openCancelMaterialModal = () => {
    setIsCancelModalOpen(true);
  };

  const confirmCancelMaterial = () => {
    resetMaterialInfo();
    setIsCancelModalOpen(false);
    setRawWeightFormStatus("");
    setRawWeightFormMessage("");
    setWasteWeightFormStatus("");
    setWasteWeightFormMessage("");
    setMaterialNameFormStatus("");
    setMaterialNameFormMessage("");
    props.resetImageView();
    props.closeForm();
  };

  const declineCancelMaterial = () => {
    setIsCancelModalOpen(false);
  };

  const updateNotes = (text: string) => {
    let material = { ...materialInfo };
    material.notes = text;
    setMaterialInfo(material);
  };

  const resetMaterialInfo = () => {
    setMaterialInfo({
      materialID: uuidv4(),
      name: "",
      displayName: "",
      metalID: "",
      rawWeight: "0.0",
      wasteWeight: "0.0",
      netWeight: "0.0",
      manualEntry: false,
      smallBag: 0,
      bigBag: 0,
      jumboBag: 0,
      notes: "",
      other: "",
      updateWasteWeight: false,
      scaleImageSrc: "",
      materialImageSrc: "",
      packageType: "bags",
    });
  };

  const openMetalForm = () => {
    setShowMetalForm(true);
  };

  const closeMetalForm = () => {
    setShowMetalForm(false);
  };

  const getActionButtonText = () => {
    if (props.type === "purchase") {
      return props.editMaterialData ? "Save To Purchase" : "Add To Purchase";
    }
    if (props.type === "shipment") {
      return props.editMaterialData ? "Save To Shipment" : "Add To Shipment";
    }
    if (props.type === "sale") {
      return props.editMaterialData ? "Save To Sale" : "Add To Sale";
    }
  };

  const onChange = (e: any) => {
    let material = { ...materialInfo };
    material.packageType = e.target.value;
    setMaterialInfo(material);
  };

  const getBagInfo = () => {
    let bagInfo = "(";
    if (materialInfo.smallBag > 0) {
      if (materialInfo.bigBag > 0) bagInfo += `${materialInfo.smallBag}S, `;
      else bagInfo += `${materialInfo.smallBag}S`;
    }
    if (materialInfo.bigBag > 0) {
      if (materialInfo.jumboBag > 0) bagInfo += `${materialInfo.bigBag}B, `;
      else bagInfo += `${materialInfo.bigBag}B`;
    }
    if (materialInfo.jumboBag > 0) {
      bagInfo += `${materialInfo.jumboBag}J`;
    }
    bagInfo += ")";
    if (materialInfo.smallBag === 0 && materialInfo.bigBag === 0 && materialInfo.jumboBag === 0) bagInfo = "";
    return bagInfo;
  };

  return props.showForm ? (
    <Row justify="center">
      <div className="add-material-form">
        <Row>
          <Col span={15}>
            <Row>
              <Col span={24}>
                <Row justify="start">
                  <h2>Add Material</h2>
                </Row>
              </Col>
            </Row>
            <Row>
              <Divider style={{ margin: "6px 0" }} />
            </Row>
            <Form layout="vertical">
              <Row className="material-dropdown-container">
                <Col span={16}>
                  <Form.Item
                    label={<label className="material-form-label">Material Name*</label>}
                    validateStatus={materialNameFormStatus}
                    help={materialNameFormMessage}
                  >
                    <Select
                      showSearch
                      placeholder="Select a Material"
                      onChange={(value: any) => updateMaterialName(value)}
                      value={materialInfo.displayName ? materialInfo.displayName : null}
                      onSearch={() => {}}
                      options={materialsNameList}
                      style={{ width: "100%" }}
                      filterOption={(input: any, option: any) => {
                        return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Row justify="end" style={{ marginTop: "32px" }}>
                    <Button
                      className="manual-entry-button"
                      icon={<PlusOutlined />}
                      type="default"
                      onClick={() => openMetalForm()}
                      style={{ width: "80%" }}
                    >
                      New Material
                    </Button>
                  </Row>
                </Col>
              </Row>
              <Row style={{ margin: "20px 0" }}>
                <Col span={12}>
                  {enableManualEntry ? (
                    <Form.Item
                      label={<label className="material-form-label">Raw Weight*</label>}
                      validateStatus={rawWeightFormStatus}
                      help={rawWeightFormMessage}
                    >
                      <Input
                        placeholder="0.0"
                        style={{ width: "150px", marginRight: "5px" }}
                        value={materialInfo.rawWeight}
                        onChange={(e) => {
                          updateRawWeight(e.target.value, true);
                        }}
                      />
                      <span>kg</span>
                    </Form.Item>
                  ) : (
                    <>
                      <Row>
                        <h3>Raw Weight*</h3>
                      </Row>
                      <Row>
                        <h3 style={{ fontSize: "16px" }}>{materialInfo.rawWeight} kg</h3>
                      </Row>
                      <Row>
                        <h3 style={{ fontSize: "16px", color: "red" }}>{rawWeightFormMessage}</h3>
                      </Row>
                    </>
                  )}
                </Col>
                <Col span={12}>
                  <Row justify="end">
                    <Button
                      type="primary"
                      icon={<AimOutlined />}
                      style={{ width: "80%", height: "60px" }}
                      onClick={() => recordWeightEntry()}
                      disabled={props.scaleSerialPort === null ? true : false}
                    >
                      Record
                    </Button>
                  </Row>
                </Col>
              </Row>
              <Row align="middle" style={{ margin: "20px 0" }}>
                <Col span={12}>
                  <Row>
                    <Form.Item
                      label={<label className="material-form-label">Waste Weight*</label>}
                      validateStatus={wasteWeightFormStatus}
                      help={wasteWeightFormMessage}
                    >
                      <Input
                        placeholder="0.0"
                        style={{ width: "150px", marginBottom: "10px", marginRight: "5px" }}
                        value={materialInfo.wasteWeight}
                        // onClick={(e) =>
                        //   e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
                        // }
                        onChange={(e) => updateWasteWeight(e.target.value)}
                      />
                      <span>kg</span>
                    </Form.Item>
                  </Row>
                  <Row>
                    <Col span={12}>
                      <Row style={{ margin: "10px 0" }} className="material-form-label">
                        Net Weight (kg)*
                      </Row>
                      <Row style={{ fontWeight: "bold", marginBottom: "15px" }}>{materialInfo.netWeight} kg</Row>
                    </Col>
                  </Row>
                </Col>
                <Col span={12}>
                  <Row>
                    <h3>Bags</h3>
                  </Row>
                  <Row align="middle">
                    <Col span={8}>
                      <Row justify="start">Small Bags</Row>
                    </Col>
                    <Col span={16}>
                      <Row justify="center" align="middle">
                        <MinusCircleOutlined
                          style={{ fontSize: "20px" }}
                          onClick={() => decreaseBagCount("smallBag")}
                        />
                        <Input
                          placeholder="0"
                          value={materialInfo.smallBag}
                          onChange={(e) => updateBagInputCount("smallBag", e.target.value)}
                          style={{ width: "60px", textAlign: "center", margin: "0 15px" }}
                        />
                        <PlusCircleOutlined style={{ fontSize: "20px" }} onClick={() => increaseBagCount("smallBag")} />
                      </Row>
                    </Col>
                  </Row>
                  <Row align="middle" style={{ margin: "15px 0" }}>
                    <Col span={8}>
                      <Row justify="start">Big Bags</Row>
                    </Col>
                    <Col span={16}>
                      <Row justify="center" align="middle">
                        <MinusCircleOutlined style={{ fontSize: "20px" }} onClick={() => decreaseBagCount("bigBag")} />
                        <Input
                          placeholder="0"
                          value={materialInfo.bigBag}
                          onChange={(e) => updateBagInputCount("bigBag", e.target.value)}
                          style={{ width: "60px", textAlign: "center", margin: "0 15px" }}
                        />
                        <PlusCircleOutlined style={{ fontSize: "20px" }} onClick={() => increaseBagCount("bigBag")} />
                      </Row>
                    </Col>
                  </Row>
                  <Row align="middle" style={{ margin: "15px 0" }}>
                    <Col span={8}>
                      <Row justify="start">Jumbo Bags</Row>
                    </Col>
                    <Col span={16}>
                      <Row justify="center" align="middle">
                        <MinusCircleOutlined
                          style={{ fontSize: "20px" }}
                          onClick={() => decreaseBagCount("jumboBag")}
                        />
                        <Input
                          placeholder="0"
                          value={materialInfo.jumboBag}
                          onChange={(e) => updateBagInputCount("jumboBag", e.target.value)}
                          style={{ width: "60px", textAlign: "center", margin: "0 15px" }}
                        />
                        <PlusCircleOutlined style={{ fontSize: "20px" }} onClick={() => increaseBagCount("jumboBag")} />
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
              {/* {props.type === "shipment" && ( */}
              <Row>
                <h3 style={{ margin: 0 }}>Package Format</h3>
              </Row>
              <Row justify="start" style={{ margin: "15px 0" }}>
                <Radio.Group onChange={onChange} value={materialInfo.packageType}>
                  <Radio value={"bundle"}>Bundle</Radio>
                  <Radio value={"bags"}>Small/Big Bags</Radio>
                  <Radio value={"jumbo"}>Jumbo Bag</Radio>
                  <Radio value={"pallet"}>Pallet</Radio>
                  <Radio value={"barrel"}>Barrel</Radio>
                  <Radio value={"loose"}>Loose</Radio>
                </Radio.Group>
              </Row>
              {/* )} */}
            </Form>
            <Row justify="center" style={{ margin: "20px 0" }}>
              <Col span={12}>
                <Row justify="start">
                  <Button
                    type="default"
                    style={{ width: "95%" }}
                    icon={<EditOutlined />}
                    onClick={() => updateManualEntry()}
                  >
                    Manual Entry
                  </Button>
                </Row>
              </Col>
              <Col span={12}>
                <Row justify="end">
                  <Button
                    type="default"
                    style={{ width: "95%" }}
                    icon={<CameraOutlined />}
                    onClick={() => captureImages()}
                    // disabled={props.scaleSerialPort === null ? true : false}
                  >
                    Manual Capture
                  </Button>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Row style={{ margin: "10px 0" }}>Notes</Row>
                <Row>
                  <TextArea
                    showCount
                    style={{ height: 50 }}
                    onChange={(e) => updateNotes(e.target.value)}
                    placeholder="Enter material notes"
                  />
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={1}>
            <Divider type="vertical" style={{ height: "100%", marginLeft: "20px" }} />
          </Col>
          <Col span={8}>
            <Row justify="end">
              <CloseCircleOutlined onClick={() => closeForm()} />
            </Row>

            <Row>
              <h3>{materialInfo.displayName}</h3>
            </Row>
            <Row>
              <Divider style={{ marginTop: "0", marginBottom: "20px" }} />
            </Row>
            <Row>
              <Col span={5}>
                <h3>Raw:</h3>
              </Col>
              <Col span={5}>
                <Row justify="end">
                  <h3>{Number(materialInfo.rawWeight).toFixed(1)}</h3>
                </Row>
              </Col>
              <Col span={3}>
                <Row justify="center">
                  <h3>kg</h3>
                </Row>
              </Col>
              <Col span={10}>
                <Row justify="center">
                  <h3>{getBagInfo()}</h3>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col span={5}>
                <h3>Waste:</h3>
              </Col>
              <Col span={5}>
                <Row justify="end">
                  <h3>- {Number(materialInfo.wasteWeight).toFixed(1)}</h3>
                </Row>
              </Col>
              <Col span={3}>
                <Row justify="center">
                  <h3>kg</h3>
                </Row>
              </Col>
            </Row>
            <Row>
              <Divider style={{ margin: "5px 0", backgroundColor: "black" }} />
            </Row>
            <Row>
              <Col span={5}>
                <h3>Net:</h3>
              </Col>
              <Col span={5}>
                <Row justify="end">
                  <h3>{Number(materialInfo.netWeight).toFixed(1)}</h3>
                </Row>
              </Col>
              <Col span={3}>
                <Row justify="center">
                  <h3>kg</h3>
                </Row>
              </Col>
            </Row>
            {(materialInfo.scaleImageSrc || props.scaleImageSrc) && (
              <>
                <Row>
                  <h3>Scale Image</h3>
                </Row>
                <Row justify="center">
                  <Image
                    height={150}
                    width={200}
                    src={materialInfo.scaleImageSrc || props.scaleImageSrc}
                    preview={{
                      toolbarRender: () => (
                        <>
                          <h1 style={{ color: "#000000" }}>{materialInfo.displayName}</h1>
                          <h3 style={{ color: "#000000" }}>
                            Raw Weight: {Number(materialInfo.rawWeight).toFixed(1)} kg
                          </h3>
                        </>
                      ),
                    }}
                  />
                </Row>
              </>
            )}
            {(materialInfo.materialImageSrc || props.materialImageSrc) && (
              <>
                <Row>
                  <h3>Material Image</h3>
                </Row>
                <Row justify="center">
                  <Image
                    height={150}
                    width={200}
                    src={materialInfo.materialImageSrc || props.materialImageSrc}
                    preview={{
                      toolbarRender: () => (
                        <>
                          <h1 style={{ color: "#000000" }}>{materialInfo.displayName}</h1>
                          <h3 style={{ color: "#000000" }}>
                            Raw Weight: {Number(materialInfo.rawWeight).toFixed(1)} kg
                          </h3>
                        </>
                      ),
                    }}
                  />
                </Row>
              </>
            )}
            <Row>
              {/* <Button className="cancel-button" onClick={() => openCancelMaterialModal()}>
                Cancel
              </Button> */}
              <Button
                type="primary"
                style={{ width: "100%", height: "50px", position: "absolute", bottom: "0px" }}
                icon={props.editMaterialData ? <SaveOutlined /> : <CheckOutlined />}
                onClick={() =>
                  props.editMaterialData
                    ? validateForm() && saveToMaterialsList()
                    : validateForm() && addToMaterialsList()
                }
              >
                {getActionButtonText()}
              </Button>
            </Row>
          </Col>
        </Row>
      </div>
      <div className="add-material-overlay"></div>
      <Modal
        title="Cancel Material Add"
        open={isCancelModalOpen}
        okText="Confirm"
        okType="danger"
        onOk={confirmCancelMaterial}
        onCancel={declineCancelMaterial}
      >
        <p>Are you sure you want to cancel?</p>
        <p>Your changes will not be saved.</p>
      </Modal>
      <MetalForm
        showForm={showMetalForm}
        closeForm={closeMetalForm}
        editMetalData={null}
        postMetal={props.postMetal ? props.postMetal : () => {}}
        getMetalsForm={props.getMetalsForm}
        categories={props.categories}
      />
    </Row>
  ) : null;
};

export default MaterialForm;
